import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Theme, Ui } from '@silvertours/front-shared';

const IconButton = styled(Ui.IconButton)`
  fill: ${({ theme }) => theme.colors.brand.green120};
  display: block;
  flex: none;
`;

/**
 * `$isInstant` shows the dialog at once when it opens, so the focus can move
 * into it. The panel still hides after its closing animation. The dropdown card
 * has no closing animation, so `$isDropdown` also hides it at once.
 */
const StyledDialog = styled.div<{
  show: boolean;
  topPosition: number;
  $isInstant?: boolean;
  $isDropdown?: boolean;
}>`
  position: fixed;
  inset: 0;
  top: ${({ topPosition }) => topPosition}px;
  z-index: 20;

  ${({ show, $isInstant, $isDropdown }) => css`
    visibility: ${show ? 'visible' : 'hidden'};
    transition: visibility
      ${(show && $isInstant) || $isDropdown ? '0s' : '0.3s'};
  `}
`;

const StyledDialogPanel = styled.div<{ show: boolean }>`
  display: flex;
  align-items: start;
  overflow: hidden;
  background-color: white;

  ${({ show }) => css`
    max-height: ${show ? '100%' : 0};
    transition: max-height 0.15s ${show ? 'ease-in' : 'ease-out'};
  `}
`;

/**
 * Dims the page below the header. It is transparent behind the dropdown card of
 * the floating header, but a click on it still closes the menu.
 */
const BackgroundOverlay = styled.div<{
  show: boolean;
  $isTransparent?: boolean;
}>`
  background-color: ${({ theme }) => theme.colors.neutral.grey160};
  position: relative;
  height: 100%;
  inset: 0;
  ${({ show, $isTransparent }) => css`
    opacity: ${show && !$isTransparent ? 0.7 : 0};
    transition: opacity 0.3s ${show ? 'ease-in' : 'ease-out'};
  `}
`;

const PanelContainer = styled.div`
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 20;
  max-height: 100%;
  overflow-y: auto;
`;

const GridContainer = styled.div`
  display: grid;
`;

const FlexContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

/**
 * The floating header has the close button above the menu entries: flush
 * below the header, and the first entry 40px below the panel edge.
 */
const StyledContent = styled.div<{
  $isFloating?: boolean;
}>`
  padding-top: ${({ theme }) => theme.spacing[50]};
  flex: 1;

  ${({ $isFloating, theme }) =>
    $isFloating &&
    css`
      padding: ${theme.spacing[70]} ${theme.spacing[40]} ${theme.spacing[40]};
    `}
`;

/**
 * The dropdown card of the floating header from md. It is aligned with the
 * right edge of the header bar (same frame padding and maximum width).
 * The container fills the space below the header. In a short window, the
 * card does not become higher than this space and scrolls.
 */
const DropdownContainer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 20;
  padding: 0 ${({ theme }) => theme.spacing[60]}
    ${({ theme }) => theme.spacing[40]};
  pointer-events: none;
`;

const DropdownFrame = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  box-sizing: border-box;
  height: 100%;
  max-width: ${Theme.getBreakpoint('xl')}px;
  margin: 0 auto;
  padding-top: ${({ theme }) => theme.spacing[20]};
`;

const DropdownCard = styled.div`
  position: relative;
  max-height: 100%;
  overflow: hidden auto;
  border-radius: ${({ theme }) => theme.radius.md};
  background-color: ${({ theme }) => theme.colors.neutral.white100};
  box-shadow: ${({ theme }) => theme.elevation.high};
  pointer-events: auto;
`;

/**
 * The submenu of the content covers this element. It is the whole content
 * height, so the submenu also covers the links when the card scrolls.
 */
const DropdownContent = styled.div`
  position: relative;
  padding: ${({ theme }) => `${theme.spacing[40]} ${theme.spacing[50]}`};
`;

const CloseButton = styled(Ui.IconButton, {
  shouldForwardProp: prop => prop !== '$isFloating',
})<{ $isFloating?: boolean }>`
  position: absolute;
  top: ${({ $isFloating, theme }) => ($isFloating ? 0 : theme.spacing[40])};
  right: ${({ theme }) => theme.spacing[40]};
  margin-right: ${({ $isFloating, theme }) =>
    $isFloating ? 0 : theme.spacing[20]};
`;

export {
  BackgroundOverlay,
  CloseButton,
  FlexContainer,
  DropdownCard,
  DropdownContainer,
  DropdownContent,
  DropdownFrame,
  GridContainer,
  IconButton,
  StyledContent,
  StyledDialog,
  StyledDialogPanel,
  PanelContainer,
};
